import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { reportingLeLensPath } from '~/consts/reportingLeLensApi';

import type { LensDataset, LensResponse } from '~/types/leLens';

import { requestWithTimeout } from '~/utils/requestWithTimeout';
import { resolveReportingLeLensUrl } from '~/utils/resolveReportingLeLensUrl';

import { reportingLeLensQueryKeys } from './useGetReportingLeLens';

export type DatasetRevision = {
  version: number;
  definition: Pick<LensDataset, 'sql'> & Partial<LensDataset>;
  createdBy: string;
  createdAt: string;
};

const historyKey = (lensId: string, datasetId: string) =>
  [...reportingLeLensQueryKeys.detail(lensId), 'history', datasetId] as const;
const datasetPath = (lensId: string, datasetId: string) =>
  `${reportingLeLensPath(lensId)}/datasets/${encodeURIComponent(datasetId)}`;

export function useLensDatasetHistory(lensId: string, datasetId: string) {
  return useQuery({
    queryKey: historyKey(lensId, datasetId),
    queryFn: ({ signal }) =>
      requestWithTimeout<{ revisions: Array<DatasetRevision>; currentVersion: number }>(
        resolveReportingLeLensUrl(`${datasetPath(lensId, datasetId)}/history`),
        { signal },
        'Version history took too long to load. Try again.',
      ),
    retry: false,
  });
}

export function useRestoreLensDataset(lensId: string, datasetId: string) {
  const client = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: (input: { version: number; lensVersion: number; datasetVersion: number }) =>
      requestWithTimeout<LensResponse>(
        resolveReportingLeLensUrl(`${datasetPath(lensId, datasetId)}/restore`),
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input) },
        'Restore has not been confirmed. Reload the Lens before trying again.',
      ),
    onSuccess: async ({ lens }) => {
      const version = lens.spec.datasets?.find((entry) => entry.id === datasetId)?.version;
      enqueueSnackbar(`Dataset restored as version ${version}`, { variant: 'success' });
      await client.invalidateQueries({ queryKey: reportingLeLensQueryKeys.detail(lensId) });
      await client.invalidateQueries({ queryKey: reportingLeLensQueryKeys.lists() });
    },
    retry: false,
  });
}
